import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CategoryBreakdown {
  categoryTag: string;
  categoryName: string;
  earned: number;
  required: number;
  isMet: boolean;
  max?: number;
  effective?: number;
}

interface CycleCompliance {
  cycleId: string;
  cycleName: string;
  professionID: string;
  professionName: string;
  stateID: number;
  stateName: string;
  totalEarned: number;
  totalRequired: number;
  percentComplete: number;
  isMet: boolean;
  categories: CategoryBreakdown[];
  warnings: string[];
  daysRemaining: number;
  cycleStartDate: string;
  cycleEndDate: string;
}

interface MemberLicense {
  _id: string;
  professionID: string;
  stateID: number;
  licenseNumber: string;
  status: string;
}

interface MemberReport {
  memberId: string;
  userId?: string;
  name: string;
  email: string;
  role: string;
  memberType: string;
  inviteStatus: string;
  professionID: string | null;
  licenses: MemberLicense[];
  compliance: CycleCompliance[];
}

interface ReportData {
  companyName: string;
  companyStreet: string;
  companyCity: string;
  companyAddressState: string;
  companyZip: string;
  companyPhone: string;
  companyWebsite: string;
  generatedAt: string;
  members: MemberReport[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

type MemberStatus = 'compliant' | 'at-risk' | 'in-progress' | 'pending' | 'none';

function getMemberStatus(m: MemberReport): MemberStatus {
  if (m.inviteStatus === 'pending') return 'pending';
  if (!m.compliance.length) return 'none';
  if (m.compliance.every(c => c.isMet)) return 'compliant';
  if (m.compliance.some(c => !c.isMet && c.daysRemaining >= 0 && c.daysRemaining <= 90)) return 'at-risk';
  return 'in-progress';
}

const STATUS_META: Record<MemberStatus, { label: string; bgClass: string; textClass: string; dotColor: string }> = {
  compliant:    { label: 'Fully Compliant', bgClass: 'bg-success',   textClass: 'text-white', dotColor: '#198754' },
  'at-risk':    { label: 'At Risk',         bgClass: 'bg-danger',    textClass: 'text-white', dotColor: '#dc3545' },
  'in-progress':{ label: 'In Progress',     bgClass: 'bg-warning',   textClass: 'text-dark',  dotColor: '#ffc107' },
  pending:      { label: 'Invite Pending',  bgClass: 'bg-secondary', textClass: 'text-white', dotColor: '#6c757d' },
  none:         { label: 'No Active Cycles',bgClass: 'bg-light border',textClass: 'text-muted',dotColor: '#adb5bd' },
};

function progressColor(pct: number, isMet: boolean): string {
  if (isMet || pct >= 100) return 'bg-success';
  if (pct >= 60) return 'bg-warning';
  return 'bg-danger';
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function creditLabel(n: number): string {
  return `${n} credit${n !== 1 ? 's' : ''}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const CycleCard: React.FC<{ cycle: CycleCompliance }> = ({ cycle }) => {
  const [expanded, setExpanded] = useState(false);
  const pct = Math.min(cycle.percentComplete, 100);
  const barClass = progressColor(pct, cycle.isMet);
  const isExpired = cycle.daysRemaining < 0;
  const isExpiringSoon = !isExpired && cycle.daysRemaining <= 90;
  const subjectCategories = cycle.categories.filter(
    c => !['LIVE_MIN', 'SELF_STUDY_MAX'].includes(c.categoryTag)
  );
  const deliveryCategories = cycle.categories.filter(
    c => ['LIVE_MIN', 'SELF_STUDY_MAX'].includes(c.categoryTag)
  );

  return (
    <div
      className="border rounded mb-3 overflow-hidden"
      style={{ breakInside: 'avoid' }}
    >
      {/* Cycle header */}
      <div
        className="d-flex align-items-start gap-3 p-3"
        style={{ background: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}
      >
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>{cycle.cycleName}</span>
            <span className="badge bg-light text-dark border" style={{ fontSize: '0.7rem' }}>
              {cycle.professionName}
            </span>
            <span className="badge bg-light text-dark border" style={{ fontSize: '0.7rem' }}>
              {cycle.stateName}
            </span>
            {cycle.isMet ? (
              <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>✓ Complete</span>
            ) : isExpired ? (
              <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>Expired</span>
            ) : isExpiringSoon ? (
              <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>⚠ Due in {cycle.daysRemaining}d</span>
            ) : (
              <span className="badge bg-info text-dark" style={{ fontSize: '0.7rem' }}>Active · {cycle.daysRemaining}d left</span>
            )}
          </div>
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
            {fmtDate(cycle.cycleStartDate)} – {fmtDate(cycle.cycleEndDate)}
          </div>
        </div>
        <div className="text-end flex-shrink-0">
          <div className="fw-bold" style={{ fontSize: '1rem', color: cycle.isMet ? '#198754' : '#0f1f3d' }}>
            {cycle.totalEarned}/{cycle.totalRequired}
          </div>
          <div className="text-muted" style={{ fontSize: '0.7rem' }}>credits</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-3 py-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="small text-muted">{pct}% complete</span>
          <span className="small text-muted">
            {cycle.totalRequired - Math.min(cycle.totalEarned, cycle.totalRequired) > 0
              ? `${cycle.totalRequired - cycle.totalEarned > 0 ? cycle.totalRequired - cycle.totalEarned : 0} more needed`
              : 'Requirement met'}
          </span>
        </div>
        <div className="progress" style={{ height: 10, borderRadius: 6 }}>
          <div
            className={`progress-bar ${barClass}`}
            style={{ width: `${pct}%`, borderRadius: 6, transition: 'width 0.4s ease' }}
          />
        </div>
      </div>

      {/* Category pills */}
      {subjectCategories.length > 0 && (
        <div className="px-3 pb-2 d-flex flex-wrap gap-2">
          {subjectCategories.map(cat => (
            <div
              key={cat.categoryTag}
              className="d-flex align-items-center gap-1 rounded px-2 py-1"
              style={{
                background: cat.isMet ? '#d1e7dd' : cat.earned > 0 ? '#fff3cd' : '#f8d7da',
                fontSize: '0.72rem',
                lineHeight: 1.3,
              }}
            >
              <span>{cat.isMet ? '✓' : '○'}</span>
              <span className="fw-medium">{cat.categoryName}</span>
              <span className="text-muted">
                {cat.earned}/{cat.required}
                {cat.max !== undefined ? ` (max ${cat.max})` : ''}
              </span>
            </div>
          ))}
          {deliveryCategories.map(cat => (
            <div
              key={cat.categoryTag}
              className="d-flex align-items-center gap-1 rounded px-2 py-1"
              style={{
                background: cat.isMet ? '#d1e7dd' : '#fff3cd',
                fontSize: '0.72rem',
                lineHeight: 1.3,
              }}
            >
              <span>{cat.isMet ? '✓' : '⚠'}</span>
              <span className="fw-medium">{cat.categoryName}</span>
              <span className="text-muted">{cat.earned}/{cat.required}</span>
            </div>
          ))}
        </div>
      )}

      {/* Warnings (expandable) */}
      {cycle.warnings.length > 0 && (
        <div className="px-3 pb-2 no-print">
          <button
            className="btn btn-link p-0 small text-muted"
            style={{ fontSize: '0.75rem', textDecoration: 'none' }}
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? '▲ Hide' : '▼ Show'} {cycle.warnings.length} note{cycle.warnings.length !== 1 ? 's' : ''}
          </button>
          {expanded && (
            <ul className="mt-1 mb-0 ps-3" style={{ fontSize: '0.75rem', color: '#856404' }}>
              {cycle.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const MemberSection: React.FC<{ member: MemberReport; stateMap: Record<number, string> }> = ({ member, stateMap }) => {
  const status = getMemberStatus(member);
  const meta = STATUS_META[status];
  const isPending = member.inviteStatus === 'pending';

  return (
    <div
      className="card shadow-sm mb-4"
      style={{ breakInside: 'avoid' }}
    >
      {/* Member header */}
      <div className="card-header d-flex align-items-center gap-3 py-3">
        {/* Status dot */}
        <div
          style={{
            width: 12, height: 12, borderRadius: '50%',
            background: meta.dotColor, flexShrink: 0,
          }}
        />
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="fw-bold" style={{ fontSize: '0.95rem' }}>{member.name}</span>
            <span className={`badge ${member.memberType === 'managed' ? 'bg-secondary' : 'bg-info text-dark'}`} style={{ fontSize: '0.65rem' }}>
              {member.memberType}
            </span>
            {member.role === 'owner' && (
              <span className="badge bg-primary" style={{ fontSize: '0.65rem' }}>owner</span>
            )}
            <span className={`badge ${meta.bgClass} ${meta.textClass}`} style={{ fontSize: '0.65rem' }}>
              {meta.label}
            </span>
          </div>
          <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
            {member.email}
            {member.professionID && (
              <span className="ms-2 badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>
                {member.professionID}
              </span>
            )}
          </div>
        </div>
        {member.userId && (
          <Link
            to={`/admin/compliance-report/${member.userId}?context=team`}
            className="btn btn-outline-secondary btn-sm no-print"
            style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
          >
            Full Report →
          </Link>
        )}
      </div>

      <div className="card-body py-3">
        {/* Licenses */}
        {member.licenses.length > 0 ? (
          <div className="mb-3">
            <div className="text-muted fw-semibold mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Licenses
            </div>
            <div className="d-flex flex-wrap gap-2">
              {member.licenses.map(lic => (
                <div
                  key={lic._id}
                  className="d-flex align-items-center gap-1 rounded border px-2 py-1"
                  style={{ fontSize: '0.78rem', background: '#f8f9fa' }}
                >
                  <span className="fw-semibold text-primary">{lic.professionID}</span>
                  <span className="text-muted">·</span>
                  <span>{stateMap[lic.stateID] || `State ${lic.stateID}`}</span>
                  <span className="text-muted">·</span>
                  <span className="font-monospace" style={{ fontSize: '0.72rem' }}>#{lic.licenseNumber}</span>
                  <span className={`badge ms-1 ${lic.status === 'active' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.6rem' }}>
                    {lic.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-muted small mb-3">No licenses on file.</div>
        )}

        {/* Compliance cycles */}
        <div className="text-muted fw-semibold mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Active Compliance Cycles
        </div>
        {isPending ? (
          <div className="text-muted small">Member hasn't accepted their invite yet.</div>
        ) : member.compliance.length === 0 ? (
          <div className="text-muted small">No active cycles. Add a cycle to begin compliance tracking.</div>
        ) : (
          member.compliance.map(cycle => (
            <CycleCard key={cycle.cycleId} cycle={cycle} />
          ))
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const TeamComplianceReport: React.FC = () => {
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stateMap, setStateMap] = useState<Record<number, string>>({});

  useEffect(() => {
    Promise.all([
      api.get('/api/company/report'),
      api.get('/api/states').catch(() => ({ data: [] })),
    ])
      .then(([rpt, states]) => {
        setData(rpt.data);
        const map: Record<number, string> = {};
        (states.data || []).forEach((s: any) => { map[s.stateID] = s.state; });
        setStateMap(map);
      })
      .catch(e => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="container mt-5 text-center">
      <div className="spinner-border text-primary" role="status" />
      <p className="text-muted mt-3 small">Loading compliance data for all members…</p>
    </div>
  );

  if (error) return (
    <div className="container mt-4">
      <div className="alert alert-danger">{error}</div>
      <Link to="/team" className="btn btn-outline-secondary btn-sm">← Back</Link>
    </div>
  );

  if (!data) return null;

  // Summary stats
  const statusCounts = data.members.reduce(
    (acc, m) => {
      const s = getMemberStatus(m);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    {} as Record<MemberStatus, number>
  );

  const generatedDate = new Date(data.generatedAt).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <>
      {/* Print CSS */}
      <style>{`
        @media print {
          @page { margin: 0.65in 0.75in; }
          .navbar, nav.navbar { display: none !important; }
          footer, [class*="footer"] { display: none !important; }
          .no-print { display: none !important; }
          .container { max-width: 100% !important; padding: 0 !important; }
          .card { box-shadow: none !important; border: 1px solid #dee2e6 !important; }
          .progress-bar { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .badge { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          [style*="background"] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .member-section, .card { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <div className="container mt-4 mb-5" style={{ maxWidth: 900 }}>

        {/* ── Screen toolbar (hidden on print) ── */}
        <div className="d-flex justify-content-between align-items-center mb-4 no-print">
          <Link to="/team" className="btn btn-outline-secondary btn-sm">← Team Dashboard</Link>
          <button className="btn btn-primary btn-sm" onClick={() => window.print()}>
            ⬇ Print / Save PDF
          </button>
        </div>

        {/* ── Professional report letterhead (screen + print) ── */}
        <div style={{ borderTop: '4px solid #0f1f3d', paddingTop: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

            {/* Left: company identity */}
            <div>
              <div style={{
                fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
                fontSize: '1.45rem',
                fontWeight: 700,
                color: '#000',
                letterSpacing: '-0.01em',
                lineHeight: 1.15,
                marginBottom: '0.45rem',
              }}>
                {data.companyName}
              </div>
              <div style={{
                fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
                fontSize: '0.78rem',
                color: '#222',
                lineHeight: 1.7,
              }}>
                {data.companyStreet && <div>{data.companyStreet}</div>}
                {(data.companyCity || data.companyAddressState || data.companyZip) && (
                  <div>
                    {[data.companyCity, data.companyAddressState].filter(Boolean).join(', ')}
                    {data.companyZip ? ` ${data.companyZip}` : ''}
                  </div>
                )}
                {data.companyPhone && <div>{data.companyPhone}</div>}
                {data.companyWebsite && <div>{data.companyWebsite}</div>}
              </div>
            </div>

            {/* Right: CEC Track mark */}
            <div style={{ textAlign: 'right', fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }}>
              <div style={{
                fontSize: '0.6rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#888',
                marginBottom: '0.2rem',
              }}>
                Prepared by
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f1f3d', letterSpacing: '-0.01em' }}>
                CEC Track
              </div>
              <div style={{ fontSize: '0.68rem', color: '#777' }}>cectrack.com</div>
            </div>
          </div>

          {/* Divider + document title row */}
          <div style={{
            borderTop: '1px solid #bbb',
            marginTop: '1.1rem',
            paddingTop: '0.7rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}>
            <div style={{
              fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
              fontSize: '0.68rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#000',
            }}>
              Team Continuing Education Compliance Report
            </div>
            <div style={{
              fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
              fontSize: '0.68rem',
              color: '#555',
              whiteSpace: 'nowrap',
            }}>
              Generated {generatedDate}
            </div>
          </div>
        </div>

        {/* ── Summary bar ── */}
        <div className="card shadow-sm mb-4">
          <div className="card-body py-3">
            <div className="row g-3 text-center">
              <div className="col-6 col-sm-3">
                <div className="fw-bold" style={{ fontSize: '1.6rem', color: '#0f1f3d' }}>
                  {data.members.length}
                </div>
                <div className="text-muted small">Total Members</div>
              </div>
              <div className="col-6 col-sm-3">
                <div className="fw-bold" style={{ fontSize: '1.6rem', color: '#198754' }}>
                  {statusCounts['compliant'] || 0}
                </div>
                <div className="text-muted small">Fully Compliant</div>
              </div>
              <div className="col-6 col-sm-3">
                <div className="fw-bold" style={{ fontSize: '1.6rem', color: '#ffc107' }}>
                  {statusCounts['in-progress'] || 0}
                </div>
                <div className="text-muted small">In Progress</div>
              </div>
              <div className="col-6 col-sm-3">
                <div className="fw-bold" style={{ fontSize: '1.6rem', color: '#dc3545' }}>
                  {statusCounts['at-risk'] || 0}
                </div>
                <div className="text-muted small">At Risk</div>
              </div>
            </div>

            {/* Visual compliance bar */}
            {data.members.length > 0 && (
              <div className="mt-3">
                <div className="progress" style={{ height: 8, borderRadius: 4 }}>
                  <div
                    className="progress-bar bg-success"
                    style={{ width: `${((statusCounts['compliant'] || 0) / data.members.length) * 100}%` }}
                    title="Fully Compliant"
                  />
                  <div
                    className="progress-bar bg-warning"
                    style={{ width: `${((statusCounts['in-progress'] || 0) / data.members.length) * 100}%` }}
                    title="In Progress"
                  />
                  <div
                    className="progress-bar bg-danger"
                    style={{ width: `${((statusCounts['at-risk'] || 0) / data.members.length) * 100}%` }}
                    title="At Risk"
                  />
                  <div
                    className="progress-bar bg-secondary"
                    style={{ width: `${(((statusCounts['pending'] || 0) + (statusCounts['none'] || 0)) / data.members.length) * 100}%` }}
                    title="Pending/No Cycles"
                  />
                </div>
                <div className="d-flex gap-3 mt-2" style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                  <span><span style={{ color: '#198754' }}>●</span> Compliant</span>
                  <span><span style={{ color: '#ffc107' }}>●</span> In Progress</span>
                  <span><span style={{ color: '#dc3545' }}>●</span> At Risk</span>
                  <span><span style={{ color: '#6c757d' }}>●</span> Pending/No Cycles</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Member sections ── */}
        {data.members.length === 0 ? (
          <div className="text-center text-muted py-5">
            No members in this team yet.
          </div>
        ) : (
          data.members.map(member => (
            <MemberSection key={member.memberId} member={member} stateMap={stateMap} />
          ))
        )}

        <div className="text-center text-muted small mt-4 pt-3 border-top no-print">
          CEC Track · Team Compliance Report · {data.companyName}
        </div>
      </div>
    </>
  );
};

export default TeamComplianceReport;
